import { type Ref } from 'vue'
import { useRoute } from 'vue-router'

import { useAiAssistantStore } from '@/stores/aiAssistant'
import type { AiUiContext } from '@/stores/aiAssistant/types'

/**
 * All company-settings fields that the AI can read and propose changes for.
 * Dot-paths that resolve against the company data object returned by CompanyData.gql.
 */
const SETTINGS_VISIBLE_FIELDS: string[] = [
  // contact / address
  'name',
  'address.street',
  'address.zip',
  'address.city',
  'address.country',
  'ambassador.email',
  'ambassador.phone',
  // website links
  'links.website',
  'links.imprint',
  'links.privacy',
  'links.about',
  // brand colours
  'theme.colors.primary',
  'theme.colors.secondary',
  // additional settings
  'slogan',
  'commission',
  'regionName',
]

/**
 * Safely resolves a dot-path string against a nested object.
 * e.g. getByPath({ address: { city: 'Munich' } }, 'address.city') → 'Munich'
 */
function getByPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc !== null && acc !== undefined && typeof acc === 'object') {
      return (acc as Record<string, unknown>)[key]
    }
    return undefined
  }, obj)
}

interface UseSettingsAiContextOptions {
  /** Reactive ref kept in sync with the company object from CompanyData.gql */
  companyRef: Ref<Record<string, unknown> | null>
}

/**
 * Builds and syncs the AI UI context for the company-settings page.
 * All sections are visible simultaneously (unlike onboarding's step-by-step view),
 * so we report a single 'settings' context with all editable fields.
 */
export function useSettingsAiContext({ companyRef }: UseSettingsAiContextOptions) {
  const aiStore = useAiAssistantStore()
  const route = useRoute()

  const buildContext = (): AiUiContext => {
    const company = companyRef.value ?? {}
    const currentValues: Record<string, unknown> = {}

    for (const field of SETTINGS_VISIBLE_FIELDS) {
      currentValues[field] = getByPath(company as Record<string, unknown>, field)
    }

    return {
      currentRoute: String(route.name ?? route.path),
      activeStepId: 'settings-company',
      stepIndex: 0,
      stepTitle: 'Company Settings',
      visibleFields: SETTINGS_VISIBLE_FIELDS,
      currentValues,
    }
  }

  /**
   * Pushes the latest context snapshot to the AI store.
   * Call this whenever the company data changes (e.g. after a successful mutation).
   */
  const syncContext = (): void => {
    aiStore.setUiContext(buildContext())
  }

  /**
   * Compatible with the AiAssistantDrawer `contextLoader` prop.
   * Also writes to the store so the context is available before the first message.
   */
  const contextLoader = (): string => {
    const ctx = buildContext()
    aiStore.setUiContext(ctx)
    return JSON.stringify(ctx, null, 2)
  }

  return { contextLoader, syncContext, buildContext }
}
